/**
 * Tokko Broker API client
 * Docs: https://developers.tokkobroker.com/
 *
 * Si VITE_TOKKO_API_KEY está vacío → usa mock data automáticamente.
 */
import { MOCK_PROPERTIES, MOCK_AGENT } from './mock.js';
import { TOKKO_TYPE_MAP, TOKKO_OP_MAP } from '../data/constants.js';

const API_KEY  = import.meta.env.VITE_TOKKO_API_KEY;
const BASE_URL = import.meta.env.VITE_TOKKO_BASE_URL || 'https://tokkobroker.com/api/v1';
const USE_MOCK = !API_KEY || API_KEY === 'your_tokko_api_key_here';

// ─── adapter: normaliza respuesta Tokko → modelo interno ────────────────────
export function adapt(raw) {
  const op = raw.operations?.[0] || {};
  const price = op.prices?.[0] || {};
  const opTypeRaw = op.operation_type;           // '1'=venta,'2'=alquiler,'3'=pozo
  const opType = opTypeRaw === '2' ? 'alquiler' : 'venta';
  const typeRaw = raw.property_type?.id;
  const type = TOKKO_TYPE_MAP[typeRaw] || 'departamento';

  return {
    id:            String(raw.id || raw.reference_code),
    ref:           raw.reference_code || '—',
    slug:          raw.slug || String(raw.id),
    title:         raw.title || raw.address || 'Propiedad',
    type,
    operationType: opType,
    status:        raw.status || 'usado',
    price:         Number(price.price) || 0,
    currency:      price.currency || 'USD',
    expenses:      Number(raw.expenses) || 0,
    neighborhood:  raw.location?.name || '',
    address:       raw.address || '',
    lat:           Number(raw.geo_lat)  || null,
    lng:           Number(raw.geo_long) || null,
    rooms:         Number(raw.room_amount)     || 0,
    bedrooms:      Number(raw.suite_amount)    || 0,
    bathrooms:     Number(raw.bathroom_amount) || 1,
    coveredArea:   Number(raw.roofed_surface)  || 0,
    totalArea:     Number(raw.total_surface)   || 0,
    parkingSpaces: Number(raw.parking_lot_amount) || 0,
    age:           raw.age ?? null,
    floor:         raw.floor ?? null,
    description:   raw.description || '',
    images:        (raw.photos || []).map(p => ({ full: p.image, thumb: p.thumb || p.image })),
    tags:          (raw.tags || []).map(t => t.name),
    amenities:     (raw.tags_objects || []).map(t => t.name),
    featured:      raw.is_featured || false,
    development:   raw.development || null,
    agent:         MOCK_AGENT,   // Tokko devuelve agent en detalle
    isDevelopment: opTypeRaw === '3',
    creditEligible: (raw.tags || []).some(t => /cr[eé]dito/i.test(t.name)),
    prevPrice:     raw.prev_price || null,
  };
}

// ─── helpers ────────────────────────────────────────────────────────────────
function apiUrl(path, params = {}) {
  const url = new URL(BASE_URL + path);
  url.searchParams.set('key', API_KEY);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url.searchParams.set(k, v);
  });
  return url.toString();
}

async function apiFetch(url, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error('Error ' + res.status + ' al consultar Tokko');
  return res.json();
}

// ─── MOCK helpers ─────────────────────────────────────────────────────────
function mockFilter(f = {}) {
  let list = MOCK_PROPERTIES.map(adapt);
  if (f.op)    list = list.filter(p => p.operationType === f.op);
  if (f.tipo)  list = list.filter(p => p.type === f.tipo);
  if (f.zona)  list = list.filter(p => p.neighborhood?.toLowerCase().includes(f.zona.toLowerCase()));
  if (f.min)   list = list.filter(p => p.price >= Number(f.min));
  if (f.max)   list = list.filter(p => p.price <= Number(f.max));
  if (f.amb)   list = list.filter(p => p.rooms >= Number(f.amb));
  if (f.dorm)  list = list.filter(p => p.bedrooms >= Number(f.dorm));
  if (f.credito) list = list.filter(p => p.creditEligible);
  if (f.sort === 'price_asc')  list.sort((a, b) => a.price - b.price);
  if (f.sort === 'price_desc') list.sort((a, b) => b.price - a.price);
  if (f.sort === 'newest')     list.reverse();
  const offset = Number(f.offset) || 0;
  const limit  = Number(f.limit)  || 12;
  return { objects: list.slice(offset, offset + limit), meta: { total_count: list.length } };
}

// ─── PUBLIC API ─────────────────────────────────────────────────────────────

/** Buscar propiedades (con filtros) */
export async function searchProperties(filters = {}, signal) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 380)); // simula latencia
    return mockFilter(filters);
  }
  const params = {
    limit:          filters.limit  || 12,
    offset:         filters.offset || 0,
    order_by:       filters.sort   || 'score',
  };
  if (filters.op    === 'venta')    params.operation_types = '1';
  if (filters.op    === 'alquiler') params.operation_types = '2';
  if (filters.tipo) {
    const id = Object.entries(TOKKO_TYPE_MAP).find(([,v]) => v === filters.tipo)?.[0];
    if (id) params.property_types = id;
  }
  if (filters.zona)  params.location     = filters.zona;
  if (filters.min)   params.price_from   = filters.min;
  if (filters.max)   params.price_to     = filters.max;
  if (filters.amb)   params.rooms_from   = filters.amb;
  if (filters.dorm)  params.suites_from  = filters.dorm;
  if (filters.credito) params.with_tags  = 'Apto crédito';
  const data = await apiFetch(apiUrl('/property/search/', params), signal);
  return { objects: (data.objects || []).map(adapt), meta: data.meta || { total_count: 0 } };
}

/** Detalle de propiedad por id o slug */
export async function getProperty(id) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 220));
    const raw = MOCK_PROPERTIES.find(p => p.id === id || p.slug === id);
    if (!raw) throw new Error('Propiedad no encontrada');
    return adapt(raw);
  }
  const data = await apiFetch(apiUrl(`/property/${id}/`));
  return adapt(data);
}

/** Listar emprendimientos */
export async function getDevelopments() {
  if (USE_MOCK) {
    return [{
      id: 'dev01', name: 'Urban VII',
      address: 'Don Bosco 10, Bernal',
      status: 'En construcción',
      description: 'El séptimo capítulo de la serie Urban. 2 a 5 ambientes con balcón terraza y vistas al río. Entrega en 24 meses, apto crédito, plan de pago directo.',
      cover: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
    }];
  }
  const data = await apiFetch(apiUrl('/development/', { limit: 20 }));
  return (data.objects || []);
}

/** Enviar consulta a Tokko */
export async function sendContact({ name, phone, email, message, propertyId }) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 600));
    console.info('[mock] Lead enviado:', { name, phone, email, message, propertyId });
    return { ok: true };
  }
  const res = await fetch(apiUrl('/contact/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name, phone, email, message,
      property: propertyId,
      source: window.location.href,
    }),
  });
  if (!res.ok) throw new Error('No se pudo enviar la consulta. Intentá por WhatsApp.');
  return res.json();
}
